/**
 * Suppliers form hook: list/create/edit/delete suppliers and their contacts.
 * @typedef {import('../../../models/Supplier').Supplier} Supplier
 */
import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../../contexts/AuthContext';
import {
  getSuppliers,
  createSupplier,
  updateSupplier,
  deleteSupplier
} from '../../../services/supplierService';
import {
  getSupplierContacts,
  createSupplierContact,
  updateSupplierContact,
  deleteSupplierContact
} from '../../../services/supplierContactService';
import { useClickOutside, createHandleChange, clearFormAlerts } from '../../common';
import { SUPPLIERS_QUERY_KEY, LIST_STALE_TIME_MS, emptyFormData, supplierToFormData } from './supplierFormUtils';
import { useAppStore } from '../../../store/appStore';

export function useSuppliersForm() {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const suppliersForm = useAppStore((s) => s.suppliersForm);
  const setSuppliersForm = useAppStore((s) => s.setSuppliersForm);
  const setInsertBasicData = useAppStore((s) => s.setInsertBasicData);
  const {
    mode,
    formData,
    formError,
    formSuccess,
    submitting,
    deleting,
    selectedSupplier,
    supplierSearchQuery,
    showSupplierDropdown,
    supplierFieldTouched,
    contacts,
    pendingContactDeletes
  } = suppliersForm;

  const setMode = React.useCallback((v) => setSuppliersForm((prev) => ({ ...prev, mode: v })), [setSuppliersForm]);
  const setFormData = React.useCallback(
    (v) =>
      setSuppliersForm((prev) => ({
        ...prev,
        formData: typeof v === 'function' ? v(prev.formData) : v
      })),
    [setSuppliersForm]
  );
  const setFormError = React.useCallback((v) => setSuppliersForm((prev) => ({ ...prev, formError: v })), [setSuppliersForm]);
  const setFormSuccess = React.useCallback((v) => setSuppliersForm((prev) => ({ ...prev, formSuccess: v })), [setSuppliersForm]);
  const setSubmitting = React.useCallback((v) => setSuppliersForm((prev) => ({ ...prev, submitting: v })), [setSuppliersForm]);
  const setDeleting = React.useCallback((v) => setSuppliersForm((prev) => ({ ...prev, deleting: v })), [setSuppliersForm]);
  const setSelectedSupplier = React.useCallback(
    (v) => setSuppliersForm((prev) => ({ ...prev, selectedSupplier: v })),
    [setSuppliersForm]
  );
  const setSupplierSearchQuery = React.useCallback(
    (v) => setSuppliersForm((prev) => ({ ...prev, supplierSearchQuery: v })),
    [setSuppliersForm]
  );
  const setShowSupplierDropdown = React.useCallback(
    (v) => setSuppliersForm((prev) => ({ ...prev, showSupplierDropdown: v })),
    [setSuppliersForm]
  );
  const setSupplierFieldTouched = React.useCallback(
    (v) => setSuppliersForm((prev) => ({ ...prev, supplierFieldTouched: v })),
    [setSuppliersForm]
  );
  const setContacts = React.useCallback(
    (v) =>
      setSuppliersForm((prev) => ({
        ...prev,
        contacts: typeof v === 'function' ? v(prev.contacts) : v
      })),
    [setSuppliersForm]
  );
  const setPendingContactDeletes = React.useCallback(
    (v) =>
      setSuppliersForm((prev) => ({
        ...prev,
        pendingContactDeletes: typeof v === 'function' ? v(prev.pendingContactDeletes) : v
      })),
    [setSuppliersForm]
  );

  const supplierDropdownRef = React.useRef(null);

  const { data: suppliersResponse, isLoading: suppliersLoading } = useQuery({
    queryKey: SUPPLIERS_QUERY_KEY,
    queryFn: () => getSuppliers(token),
    enabled: !!token && mode === 'edit' && supplierFieldTouched,
    staleTime: LIST_STALE_TIME_MS
  });

  const { data: contactsResponse, isLoading: contactsLoading } = useQuery({
    queryKey: ['supplier-contacts', selectedSupplier?.id],
    queryFn: () => getSupplierContacts(token, selectedSupplier.id),
    enabled: !!token && !!selectedSupplier?.id
  });

  const sectionLoading = suppliersLoading || contactsLoading;
  React.useEffect(() => {
    setInsertBasicData((prev) => ({
      ...prev,
      sectionLoading: { ...prev.sectionLoading, suppliers: sectionLoading }
    }));
  }, [sectionLoading, setInsertBasicData]);

  React.useEffect(() => {
    if (mode === 'edit' && contactsResponse?.supplier_contacts != null) {
      setContacts(contactsResponse.supplier_contacts);
    }
  }, [mode, contactsResponse?.supplier_contacts]);

  React.useEffect(() => {
    if (mode === 'edit' && selectedSupplier?.id) {
      setPendingContactDeletes([]);
    }
  }, [mode, selectedSupplier?.id]);

  const suppliers = suppliersResponse?.suppliers ?? [];
  const filteredSuppliers = React.useMemo(() => {
    if (!supplierSearchQuery.trim()) return suppliers;
    const q = supplierSearchQuery.trim().toLowerCase();
    return suppliers.filter((s) => (s.name || '').toLowerCase().includes(q));
  }, [suppliers, supplierSearchQuery]);

  useClickOutside([supplierDropdownRef], () => setShowSupplierDropdown(false));

  const handleChange = React.useMemo(
    () => createHandleChange(setFormData, setFormError, setFormSuccess),
    [setFormData, setFormError, setFormSuccess]
  );

  const handleModeChange = (newMode) => {
    setMode(newMode);
    clearFormAlerts(setFormError, setFormSuccess);
    setFormData(emptyFormData());
    setSelectedSupplier(null);
    setSupplierSearchQuery('');
    setShowSupplierDropdown(false);
    setContacts([]);
    setPendingContactDeletes([]);
    if (newMode === 'create') setSupplierFieldTouched(false);
  };

  const handleSupplierSearchChange = (e) => {
    setSupplierSearchQuery(e.target.value);
    setSelectedSupplier(null);
    setShowSupplierDropdown(true);
  };

  /** @param {Supplier} supplier */
  const handleSelectSupplier = (supplier) => {
    setSelectedSupplier(supplier);
    setFormData(supplierToFormData(supplier));
    setSupplierSearchQuery(supplier.name || '');
    setShowSupplierDropdown(false);
    setContacts([]);
    clearFormAlerts(setFormError, setFormSuccess);
  };

  const addContact = () => {
    setContacts((prev) => [
      ...prev,
      {
        first_name: '',
        last_name: '',
        email: '',
        phone_number: '',
        job_title: '',
        primary_contact: false
      }
    ]);
  };

  const updateContact = (index, field, value) => {
    setContacts((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item))
    );
  };

  const removeContact = (index) => {
    const item = contacts[index];
    if (item?.id != null) {
      setPendingContactDeletes((prev) => [...prev, item.id]);
    }
    setContacts((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearFormAlerts(setFormError, setFormSuccess);
    setSubmitting(true);

    try {
      const supplierData = {
        name: formData.name?.trim() || null,
        country: formData.country?.trim() || null,
        website: formData.website?.trim() || null
      };

      if (mode === 'create') {
        const result = await createSupplier(token, supplierData);
        if (!result.success) {
          setFormError(result.error || 'Failed to create supplier');
          setSubmitting(false);
          return;
        }
        const supplierId = result.supplier?.id;
        if (supplierId != null && contacts.length > 0) {
          for (const item of contacts) {
            await createSupplierContact(token, supplierId, {
              first_name: item.first_name?.trim() || null,
              last_name: item.last_name?.trim() || null,
              email: item.email?.trim() || null,
              phone_number: item.phone_number?.trim() || null,
              job_title: item.job_title?.trim() || null,
              primary_contact: !!item.primary_contact
            });
          }
        }
        setFormSuccess('Supplier created successfully!');
        setFormData(emptyFormData());
        setContacts([]);
        queryClient.invalidateQueries({ queryKey: SUPPLIERS_QUERY_KEY });
      } else {
        if (!selectedSupplier?.id) {
          setFormError('Please select a supplier to edit');
          setSubmitting(false);
          return;
        }
        const result = await updateSupplier(token, selectedSupplier.id, supplierData);
        if (!result.success) {
          setFormError(result.error || 'Failed to update supplier');
          setSubmitting(false);
          return;
        }
        for (const contactId of pendingContactDeletes) {
          await deleteSupplierContact(token, contactId);
        }
        for (const item of contacts) {
          if (item.id != null) {
            await updateSupplierContact(token, item.id, {
              first_name: item.first_name?.trim() || null,
              last_name: item.last_name?.trim() || null,
              email: item.email?.trim() || null,
              phone_number: item.phone_number?.trim() || null,
              job_title: item.job_title?.trim() || null,
              primary_contact: !!item.primary_contact
            });
          } else {
            await createSupplierContact(token, selectedSupplier.id, {
              first_name: item.first_name?.trim() || null,
              last_name: item.last_name?.trim() || null,
              email: item.email?.trim() || null,
              phone_number: item.phone_number?.trim() || null,
              job_title: item.job_title?.trim() || null,
              primary_contact: !!item.primary_contact
            });
          }
        }
        setPendingContactDeletes([]);
        if (pendingContactDeletes.length > 0 || contacts.length > 0) {
          queryClient.invalidateQueries({
            queryKey: ['supplier-contacts', selectedSupplier.id]
          });
        }
        setFormSuccess('Changes saved successfully!');
        setFormData(emptyFormData());
        setSelectedSupplier(null);
        setSupplierSearchQuery('');
        setContacts([]);
        queryClient.invalidateQueries({ queryKey: SUPPLIERS_QUERY_KEY });
      }
    } catch (error) {
      setFormError(error.message || (mode === 'create' ? 'Failed to create supplier' : 'Failed to update supplier'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedSupplier?.id) return;
    clearFormAlerts(setFormError, setFormSuccess);
    setDeleting(true);
    try {
      const result = await deleteSupplier(token, selectedSupplier.id);
      if (result.success) {
        setFormSuccess('Supplier deleted.');
        setFormData(emptyFormData());
        setSelectedSupplier(null);
        setSupplierSearchQuery('');
        setShowSupplierDropdown(false);
        setContacts([]);
        setPendingContactDeletes([]);
        queryClient.invalidateQueries({ queryKey: SUPPLIERS_QUERY_KEY });
      } else {
        setFormError(result.error || 'Failed to delete supplier');
      }
    } catch (error) {
      setFormError(error.message || 'Failed to delete supplier');
    } finally {
      setDeleting(false);
    }
  };

  const isEditMode = mode === 'edit';

  return {
    mode,
    formData,
    formError,
    formSuccess,
    submitting,
    deleting,
    selectedSupplier,
    supplierSearchQuery,
    showSupplierDropdown,
    supplierFieldTouched,
    suppliersLoading,
    filteredSuppliers,
    supplierDropdownRef,
    handleModeChange,
    handleChange,
    handleSupplierSearchChange,
    handleSelectSupplier,
    handleSubmit,
    handleDelete,
    isEditMode,
    setSupplierFieldTouched,
    setShowSupplierDropdown,
    contacts,
    contactsLoading,
    addContact,
    updateContact,
    removeContact
  };
}
